// Cloudflare Turnstile widget wrapper.
//
// Renders the CAPTCHA via explicit-mode render. The page must load the
// Turnstile script (see index.html). When the user passes the
// challenge, the onVerify callback receives the token to send with the
// next API request. Token TTL is ~5 minutes; the widget auto-rotates
// before that and re-fires onVerify.
//
// The site key is public and safe to embed (the secret lives on the
// orbis-studio-api worker as TURNSTILE_SECRET_KEY).

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          appearance?: 'always' | 'execute' | 'interaction-only';
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

// Public site key for studio.imorbis.com / studio-api.imorbis.com.
// Mirrored by TURNSTILE_SECRET_KEY on the orbis-studio-api worker.
export const TURNSTILE_SITE_KEY = '0x4AAAAAADQW_ArhmBkn5uFj';

export function Turnstile({
  onVerify,
  onExpire,
  theme = 'light',
}: {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;

    function mount() {
      if (cancelled || !el || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onVerify(token),
        'expired-callback': () => onExpire?.(),
        'error-callback': () => onExpire?.(),
        theme,
        // Keep the widget visible ('always', the default) rather than
        // 'interaction-only'. Interaction-only hid the challenge, so users on
        // stricter networks (VPN/privacy) who were handed an interactive
        // challenge saw nothing to complete, submitted with a null token, and
        // hit a dead-end 403 ("Please complete the verification") with no
        // visible captcha to solve. A visible widget makes the step obvious.
      });
    }

    // Turnstile script loads async; poll briefly until window.turnstile
    // is defined. In practice this resolves within ~50ms once the page
    // is interactive.
    if (window.turnstile) {
      mount();
    } else {
      const id = setInterval(() => {
        if (window.turnstile) {
          clearInterval(id);
          mount();
        }
      }, 50);
      // Belt-and-suspenders: stop polling after 10s.
      setTimeout(() => clearInterval(id), 10_000);
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
    };
  }, [onVerify, onExpire, theme]);

  return <div ref={ref} />;
}
